import { useEffect, useState } from 'react';
import { useAsync, usePrevious } from '@src/hooks/core/core-hook-imports.js';
import SearchApiResponse from '@js/helpers/search-api-response.es6.js';
import { useDependenciesContext } from '@src/contexts/dependencies.jsx';
import ScheduleProviderCardUtils from '@src/js/provider-card-utils.js';
import {LuminisServiceUtils} from '@js/utils/utils-imports.es6.js';

/**
 * @typedef {object} UseProviderSearchReturn
 * @property {function} run the function that can be run to rerun the provider search
 * with the current parameters
 * @property {string} loading true if the provider search is currently running and false if
 * it either has not been started or has alread finished
 * @property {object} error the error, if any, for the provider search
 * @property {object} response the provider search results (in the form of a standard Search
 * API REST response)
 */

/**
 * Uses the Search API's Provider Search View to fetch provider results with the provided
 * filters.  When onlineSchedulingType is provided, performs custom pagination and filtering
 * to ensure full pages of providers are returned.  The number of items returned on each page
 * is determined by the pager.items_per_page property of the first page fetched from the
 * Search API.
 *
 * @param {string} search the user's searched text
 * @param {string} location the user's location
 * @param {string} specialty the requested specialty
 * @param {string} onlineSchedulingType the request online scheduling type ('in-person',
 * 'telemedicine', or 'in-person-or-telemedicine')
 * @param {number} pageNum the requested page (a zero-based index)
 * @returns {UseProviderSearchReturn}
 *
 * @example
 * const { run, loading, error, response } = useProviderSearch({ search: 'Primary Care', pageNum: 2 });
*/
const useProviderSearch = ({
  search,
  location,
  specialty,
  onlineSchedulingType,
  pageNum = 0,
}) => {
  const { searchApiService, luminisServiceUtils } = useDependenciesContext();
  const [cachedPages, setCachedPages] = useState([]);

  const prevSearch = usePrevious(search);
  const prevLocation = usePrevious(location);
  const prevSpecialty = usePrevious(specialty);
  const prevOnlineSchedulingType = usePrevious(onlineSchedulingType);

  const getData = async () => {
    setCachedPages([]);

    let pages;
    const exclusionsList = await LuminisServiceUtils.getExclusionMnemonics();

    if (onlineSchedulingType) {
      /*
        Requires custom pagination because we'll be filtering the results returned
        by the Search API based on external factors (data from the Luminis API).
        Fetch all results, filter them, and return custom pages.
      */
      const allSearchApiResults = await searchApiService.searchProviders({
        searchedText: search,
        userLocation: location,
        specialty,
        configuredForOnlineScheduling: true,
      });

      const itemsPerPage = allSearchApiResults[0].pager.items_per_page;

      let providers = allSearchApiResults.map(r => r.rows).flat();
      await ScheduleProviderCardUtils.setProviderOnlineSchedulingAvailabilityInfo(providers, luminisServiceUtils, exclusionsList);

      providers = ScheduleProviderCardUtils.filterProviders(providers, onlineSchedulingType);

      await ScheduleProviderCardUtils.setProviderLocationInfo(providers, location, searchApiService);

      ScheduleProviderCardUtils.sortProvidersByDistance(providers);
      ScheduleProviderCardUtils.sortProvidersByIsLuminisProvider(providers);

      pages = SearchApiResponse.arrayToSearchApiResponses(providers, itemsPerPage);
      setCachedPages(pages);
    }
    else {
      const singlePageSearchApiResults = await searchApiService.searchProviders({
        searchedText: search,
        userLocation: location,
        specialty,
        configuredForOnlineScheduling: false,
        pageNum,
      });

      let providers = singlePageSearchApiResults.rows;

      await ScheduleProviderCardUtils.setProviderOnlineSchedulingAvailabilityInfo(providers, luminisServiceUtils, exclusionsList);
      await ScheduleProviderCardUtils.setProviderLocationInfo(providers, location, searchApiService);

      ScheduleProviderCardUtils.sortProvidersByDistance(providers);
      ScheduleProviderCardUtils.sortProvidersByIsLuminisProvider(providers);

      pages = new Array(pageNum);
      pages[pageNum] = singlePageSearchApiResults;
      setCachedPages(pages);
    }

    return pages;
  };

  const { run, loading, error } = useAsync(getData);

  useEffect(() => {
    const runNewSearch = prevSearch !== search
      || prevLocation !== location
      || prevSpecialty !== specialty
      || prevOnlineSchedulingType !== onlineSchedulingType;

    const pageIsCached = !!cachedPages[pageNum];

    if (runNewSearch || !pageIsCached) {
      run();
    }
  }, [search, location, specialty, onlineSchedulingType, pageNum]);

  return {
    run,
    loading,
    error,
    response : cachedPages[pageNum] ?? null,
  };
};

export default useProviderSearch;
